import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Edit, Trash2, DollarSign } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const AdInventoryPricing = () => {
  const { apiCall } = useAuth();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [formData, setFormData] = useState({
    slot_id: '',
    slot_name: '',
    slot_type: 'search_placement',
    placement_location: 'homepage',
    pricing_model: 'CPM',
    cpc_rate: '',
    cpm_rate: '',
    fixed_fee: '',
    max_slots_available: 1,
    min_budget: 80,
    recommended_budget: '',
    is_active: true
  });

  useEffect(() => {
    fetchSlots();
  }, []);

  const fetchSlots = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/api/marketing/ad-inventory-pricing');
      setSlots(response);
    } catch (error) {
      toast.error('Failed to load ad inventory pricing');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (slot = null) => {
    if (slot) {
      setEditingSlot(slot);
      setFormData({
        slot_id: slot.slot_id,
        slot_name: slot.slot_name,
        slot_type: slot.slot_type,
        placement_location: slot.placement_location,
        pricing_model: slot.pricing_model,
        cpc_rate: slot.cpc_rate || '',
        cpm_rate: slot.cpm_rate || '',
        fixed_fee: slot.fixed_fee || '',
        max_slots_available: slot.max_slots_available,
        min_budget: slot.min_budget,
        recommended_budget: slot.recommended_budget || '',
        is_active: slot.is_active
      });
    } else {
      setEditingSlot(null);
      setFormData({
        slot_id: '',
        slot_name: '',
        slot_type: 'search_placement',
        placement_location: 'homepage',
        pricing_model: 'CPM',
        cpc_rate: '',
        cpm_rate: '',
        fixed_fee: '',
        max_slots_available: 1,
        min_budget: 80,
        recommended_budget: '',
        is_active: true
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Build payload based on pricing model
    const payload = {
      ...formData,
      cpc_rate: formData.pricing_model === 'CPC' && formData.cpc_rate ? parseFloat(formData.cpc_rate) : null,
      cpm_rate: formData.pricing_model === 'CPM' && formData.cpm_rate ? parseFloat(formData.cpm_rate) : null,
      fixed_fee: formData.pricing_model === 'Fixed_Fee' && formData.fixed_fee ? parseFloat(formData.fixed_fee) : null,
      min_budget: parseFloat(formData.min_budget),
      recommended_budget: formData.recommended_budget ? parseFloat(formData.recommended_budget) : null,
      max_slots_available: parseInt(formData.max_slots_available)
    };

    try {
      if (editingSlot) {
        await apiCall(`/api/marketing/ad-inventory-pricing/${editingSlot.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload)
        });
        toast.success('Ad slot updated successfully');
      } else {
        await apiCall('/api/marketing/ad-inventory-pricing', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        toast.success('Ad slot created successfully');
      }
      
      setIsModalOpen(false);
      fetchSlots();
    } catch (error) {
      toast.error(error.message || 'Failed to save ad slot');
    }
  };

  const handleDelete = async (slotId) => {
    if (!window.confirm('Are you sure you want to delete this ad slot?')) return;
    
    try {
      await apiCall(`/api/marketing/ad-inventory-pricing/${slotId}`, {
        method: 'DELETE'
      });
      toast.success('Ad slot deleted successfully');
      fetchSlots();
    } catch (error) {
      toast.error('Failed to delete ad slot');
    }
  };

  const getPricingDisplay = (slot) => {
    switch (slot.pricing_model) {
      case 'CPC':
        return `₹${slot.cpc_rate} per click`;
      case 'CPM':
        return `₹${slot.cpm_rate} per 1000 impressions`;
      case 'Fixed_Fee':
        return `₹${slot.fixed_fee} (one-time)`;
      default:
        return 'N/A';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Ad Inventory Pricing</h1>
            <p className="text-gray-600 mt-2">Configure pricing for advertising slots</p>
          </div>
          <Button onClick={() => handleOpenModal()}>
            <Plus className="h-4 w-4 mr-2" />
            Add Ad Slot
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Slots</p>
                  <p className="text-2xl font-bold">{slots.length}</p>
                </div>
                <DollarSign className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Slots</p>
                  <p className="text-2xl font-bold">{slots.filter(s => s.is_active).length}</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">CPM Slots</p>
                  <p className="text-2xl font-bold">{slots.filter(s => s.pricing_model === 'CPM').length}</p>
                </div>
                <DollarSign className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">CPC Slots</p>
                  <p className="text-2xl font-bold">{slots.filter(s => s.pricing_model === 'CPC').length}</p>
                </div>
                <DollarSign className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Slots Table */}
        <Card>
          <CardHeader>
            <CardTitle>Ad Inventory Slots</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-12 text-gray-500">Loading...</p>
            ) : slots.length === 0 ? (
              <p className="text-center py-12 text-gray-500">No ad slots configured yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slot ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pricing</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Min Budget</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {slots.map((slot) => (
                      <tr key={slot.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-mono">{slot.slot_id}</td>
                        <td className="px-4 py-3 text-sm">{slot.slot_name}</td>
                        <td className="px-4 py-3 text-sm">
                          <Badge variant="outline">{slot.slot_type}</Badge>
                        </td>
                        <td className="px-4 py-3 text-sm">{slot.placement_location}</td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <Badge className={
                              slot.pricing_model === 'CPM' ? 'bg-purple-100 text-purple-800' :
                              slot.pricing_model === 'CPC' ? 'bg-orange-100 text-orange-800' :
                              'bg-blue-100 text-blue-800'
                            }>
                              {slot.pricing_model}
                            </Badge>
                            <span className="text-gray-600">{getPricingDisplay(slot)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">₹{slot.min_budget}</td>
                        <td className="px-4 py-3 text-sm">
                          <Badge variant={slot.is_active ? 'default' : 'secondary'}>
                            {slot.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          <div className="flex gap-2 justify-end">
                            <Button variant="outline" size="sm" onClick={() => handleOpenModal(slot)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleDelete(slot.id)}>
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingSlot ? 'Edit Ad Slot' : 'Create Ad Slot'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Slot ID *</Label>
                  <Input
                    value={formData.slot_id}
                    onChange={(e) => setFormData({...formData, slot_id: e.target.value})}
                    placeholder="e.g., SLOT_HOME_BANNER_01"
                    required
                    disabled={!!editingSlot}
                  />
                </div>
                <div>
                  <Label>Slot Name *</Label>
                  <Input
                    value={formData.slot_name}
                    onChange={(e) => setFormData({...formData, slot_name: e.target.value})}
                    placeholder="e.g., Homepage Top Banner"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Slot Type *</Label>
                  <Select value={formData.slot_type} onValueChange={(value) => setFormData({...formData, slot_type: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="search_placement">Search Placement</SelectItem>
                      <SelectItem value="listing_banner">Listing Banner</SelectItem>
                      <SelectItem value="homepage_takeover">Homepage Takeover</SelectItem>
                      <SelectItem value="carousel">Carousel</SelectItem>
                      <SelectItem value="sidebar_ad">Sidebar Ad</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Placement Location *</Label>
                  <Select value={formData.placement_location} onValueChange={(value) => setFormData({...formData, placement_location: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="homepage">Homepage</SelectItem>
                      <SelectItem value="search_results">Search Results</SelectItem>
                      <SelectItem value="product_listing">Product Listing</SelectItem>
                      <SelectItem value="order_page">Order Page</SelectItem>
                      <SelectItem value="checkout">Checkout</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Pricing Model *</Label>
                <Select value={formData.pricing_model} onValueChange={(value) => setFormData({...formData, pricing_model: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CPC">CPC (Cost Per Click)</SelectItem>
                    <SelectItem value="CPM">CPM (Cost Per 1000 Impressions)</SelectItem>
                    <SelectItem value="Fixed_Fee">Fixed Fee</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.pricing_model === 'CPC' && (
                <div>
                  <Label>CPC Rate (₹) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.cpc_rate}
                    onChange={(e) => setFormData({...formData, cpc_rate: e.target.value})}
                    placeholder="e.g., 5.00"
                    required
                  />
                </div>
              )}

              {formData.pricing_model === 'CPM' && (
                <div>
                  <Label>CPM Rate (₹) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.cpm_rate}
                    onChange={(e) => setFormData({...formData, cpm_rate: e.target.value})}
                    placeholder="e.g., 100.00"
                    required
                  />
                </div>
              )}

              {formData.pricing_model === 'Fixed_Fee' && (
                <div>
                  <Label>Fixed Fee (₹) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.fixed_fee}
                    onChange={(e) => setFormData({...formData, fixed_fee: e.target.value})}
                    placeholder="e.g., 5000.00"
                    required
                  />
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Max Slots Available *</Label>
                  <Input
                    type="number"
                    value={formData.max_slots_available}
                    onChange={(e) => setFormData({...formData, max_slots_available: e.target.value})}
                    min="1"
                    required
                  />
                </div>
                <div>
                  <Label>Min Budget (₹) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.min_budget}
                    onChange={(e) => setFormData({...formData, min_budget: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Recommended Budget (₹)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.recommended_budget}
                    onChange={(e) => setFormData({...formData, recommended_budget: e.target.value})}
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="rounded"
                />
                <Label htmlFor="is_active">Active</Label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingSlot ? 'Update' : 'Create'} Slot
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdInventoryPricing;
